import type { Call, CallAction, CommandReceipt, CreateCall, DiagnosticReport } from '@bcio-voice/contracts';
import { isUuid } from './ids.js';

export type ApiFailure =
  | { readonly ok: false; readonly kind: 'http'; readonly status: number; readonly code: string; readonly message: string; readonly retryable: boolean; readonly request_id: string | null }
  /** No HTTP response: offline, DNS, TLS, abort or timeout. The server may or may not have acted. */
  | { readonly ok: false; readonly kind: 'network'; readonly message: string }
  | { readonly ok: false; readonly kind: 'invalid_response'; readonly status: number; readonly message: string };

export type ApiResult<T> = { readonly ok: true; readonly value: T } | ApiFailure;

export interface DeviceHeartbeat {
  registration_state: 'unregistered' | 'registering' | 'registered' | 'failed';
  audio_permission: 'unknown' | 'granted' | 'denied';
  client_version: string;
}

/**
 * Server control plane, injected into the engine. Implementations own authentication (cookie
 * session + CSRF for web, the desktop shell's own transport for Electron). The engine never
 * handles bearer tokens, carrier credentials or a global auth state, and never logs requests.
 */
export interface CallControlApi {
  createCall(body: CreateCall, idempotencyKey: string, signal?: AbortSignal): Promise<ApiResult<CommandReceipt>>;
  callAction(callId: string, body: CallAction, idempotencyKey: string, signal?: AbortSignal): Promise<ApiResult<CommandReceipt>>;
  getCommand(commandId: string, signal?: AbortSignal): Promise<ApiResult<CommandReceipt>>;
  getCall(callId: string, signal?: AbortSignal): Promise<ApiResult<Call>>;
  heartbeat(deviceId: string, body: DeviceHeartbeat, signal?: AbortSignal): Promise<ApiResult<unknown>>;
  /** POST /api/v1/calls/:id/diagnostics (bounded, allowlisted). Optional: older adapters simply do not report. */
  reportDiagnostics?(callId: string, report: DiagnosticReport, signal?: AbortSignal): Promise<ApiResult<unknown>>;
}

/**
 * Source of device SIP credentials. Returns the raw payload (a canonical `Device`, its `bootstrap`,
 * or a proposed v1 document); the engine validates it. Use `DeviceCredentialSource` for B1.
 */
export interface DeviceBootstrapSource {
  load(deviceId: string, signal?: AbortSignal): Promise<ApiResult<unknown>>;
}

const CALL_STATES = new Set(['created', 'ringing', 'active', 'held', 'dropping', 'ended', 'failed']);
const COMMAND_STATES = new Set(['accepted', 'dispatching', 'applied', 'failed', 'unknown']);

/** Structural check of the fields the engine depends on. The server remains the schema authority. */
export function isCall(value: unknown): value is Call {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return isUuid(v.id) && Number.isInteger(v.version) && (v.version as number) >= 1
    && typeof v.state === 'string' && CALL_STATES.has(v.state)
    && Array.isArray(v.allowed_actions) && (v.allowed_actions as unknown[]).every(a => typeof a === 'string')
    && Array.isArray(v.participants)
    && typeof v.direction === 'string' && typeof v.recording_state === 'string';
}

export function isCommandReceipt(value: unknown): value is CommandReceipt {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return isUuid(v.command_id) && typeof v.status === 'string' && COMMAND_STATES.has(v.status) && isCall(v.call);
}

export type FetchLike = (input: string, init: {
  method: string;
  headers: Record<string, string>;
  body?: string;
  credentials: 'include' | 'same-origin';
  cache: 'no-store';
  redirect: 'error';
  signal?: AbortSignal;
}) => Promise<{ status: number; json(): Promise<unknown> }>;

export interface FetchApiOptions {
  /** '' for same-origin, otherwise an https origin such as `https://voice.example`. */
  readonly baseUrl: string;
  readonly fetch: FetchLike;
  /** Current CSRF token from `GET /api/v1/auth/session`; read on every mutating request. */
  readonly csrfToken: () => string | null;
  readonly timeoutMs?: number;
  readonly credentials?: 'include' | 'same-origin';
}

function checkBase(baseUrl: string): string {
  if (baseUrl === '') return '';
  const url = new URL(baseUrl);
  const loopback = url.hostname === '127.0.0.1' || url.hostname === 'localhost';
  if ((url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) || url.pathname !== '/' || url.search || url.hash || url.username || url.password) {
    throw new Error('baseUrl must be an https origin (or loopback http for development).');
  }
  return url.origin;
}

function segment(id: string): string {
  if (!isUuid(id)) throw new Error('Resource IDs must be UUIDs.');
  return encodeURIComponent(id.toLowerCase());
}

export interface FetchCallControlApi extends CallControlApi {
  /** B1 `POST /api/v1/devices/{id}/credentials`; returns the `Device`. Rotates on every call: wrap in `DeviceCredentialSource`. */
  issueCredentials(deviceId: string, signal?: AbortSignal): Promise<ApiResult<unknown>>;
}

/** Fetch-based adapter for the B1 routes. Cookie session plus `X-CSRF-Token`; no Authorization header. */
export function createFetchCallControlApi(options: FetchApiOptions): FetchCallControlApi {
  const base = checkBase(options.baseUrl);
  const timeoutMs = options.timeoutMs ?? 10_000;

  async function request<T>(method: 'GET' | 'POST', path: string, body: unknown, idempotencyKey: string | null, guard: ((v: unknown) => v is T) | null, signal?: AbortSignal): Promise<ApiResult<T>> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (method !== 'GET') {
      const csrf = options.csrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;
      headers['Content-Type'] = 'application/json';
    }
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
    const timeout = AbortSignal.timeout(timeoutMs);
    const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;
    let response: { status: number; json(): Promise<unknown> };
    try {
      response = await options.fetch(base + path, {
        method, headers, credentials: options.credentials ?? 'include', cache: 'no-store', redirect: 'error', signal: combined,
        ...(method !== 'GET' ? { body: JSON.stringify(body ?? {}) } : {}),
      });
    } catch {
      return { ok: false, kind: 'network', message: 'The server could not be reached.' };
    }
    let payload: unknown = null;
    try { payload = response.status === 204 ? null : await response.json(); } catch { payload = null; }
    if (response.status >= 200 && response.status < 300) {
      if (guard === null) return { ok: true, value: (payload as { data?: unknown } | null)?.data as T };
      const data = (payload as { data?: unknown } | null)?.data;
      return guard(data) ? { ok: true, value: data } : { ok: false, kind: 'invalid_response', status: response.status, message: 'Response did not match the expected resource.' };
    }
    const error = (payload as { error?: Record<string, unknown> } | null)?.error;
    const text = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : null);
    return {
      ok: false, kind: 'http', status: response.status,
      code: text(error?.code, 100) ?? `http_${response.status}`,
      message: text(error?.message, 500) ?? 'The request was not completed.',
      retryable: error?.retryable === true,
      request_id: text(error?.request_id, 100),
    };
  }

  // async wrappers: an invalid ID rejects the returned promise instead of throwing synchronously.
  return {
    createCall: async (body, key, signal) => request('POST', '/api/v1/calls', body, key, isCommandReceipt, signal),
    callAction: async (callId, body, key, signal) => request('POST', `/api/v1/calls/${segment(callId)}/actions`, body, key, isCommandReceipt, signal),
    getCommand: async (commandId, signal) => request('GET', `/api/v1/commands/${segment(commandId)}`, null, null, isCommandReceipt, signal),
    getCall: async (callId, signal) => request('GET', `/api/v1/calls/${segment(callId)}`, null, null, isCall, signal),
    heartbeat: async (deviceId, body, signal) => request('POST', `/api/v1/devices/${segment(deviceId)}/heartbeat`, body, null, null, signal),
    reportDiagnostics: async (callId, report, signal) => request('POST', `/api/v1/calls/${segment(callId)}/diagnostics`, report, null, null, signal),
    issueCredentials: async (deviceId, signal) => request('POST', `/api/v1/devices/${segment(deviceId)}/credentials`, {}, null, null, signal),
  };
}
